import api from "./api";
import {
  MeterUploadRowStatus,
  type MeterUploadRowStatusValue,
  type ManualIntervention,
  type MeterUploadRow,
  type MeterUploadRowData,
} from "../utils/bulkMeterUpload/buildTrialRunRow";
import {
  MeterUploadStatus,
  type MeterUploadStatusValue,
} from "../utils/bulkMeterUpload/computeParentStatus";
import type { TrialRunServerRow } from "../utils/bulkMeterUpload/buildTrialRunRow";
import type { BulkMeterTemplateRow } from "../utils/bulkMeterUpload/buildTemplateWorkbook";
export type { BulkMeterTemplateRow };

const PARENT_STATUS_FROM_NUMBER: Record<number, MeterUploadStatusValue> = {
  0: MeterUploadStatus.Draft,
  1: MeterUploadStatus.PartiallyCompleted,
  2: MeterUploadStatus.Completed,
};

const ROW_STATUS_FROM_NUMBER: Record<number, MeterUploadRowStatusValue> = {
  0: MeterUploadRowStatus.Draft,
  1: MeterUploadRowStatus.Success,
  2: MeterUploadRowStatus.Failed,
};

// The `status` columns are smallint, so status always arrives as a number; an
// unknown value falls back to Draft.
const toParentStatus = (raw: unknown): MeterUploadStatusValue =>
  (typeof raw === "number" && PARENT_STATUS_FROM_NUMBER[raw]) || MeterUploadStatus.Draft;

const toRowStatus = (raw: unknown): MeterUploadRowStatusValue =>
  (typeof raw === "number" && ROW_STATUS_FROM_NUMBER[raw]) || MeterUploadRowStatus.Draft;

// API stores `seq` as a string (matches the decimal(10,3) column representation), but the UI
// works with it as a number for arithmetic-friendly comparisons. Translate at the wire boundary.
// Non-numeric stored values (e.g. legacy 'A1') would coerce to NaN and round-trip back as the
// literal string 'NaN'; drop them to undefined so the corruption can't propagate.
const fromApiRowData = (raw: any): MeterUploadRowData => {
  if (raw?.seq == null || raw.seq === "") {
    return { ...raw, seq: undefined };
  }
  const parsed = Number(raw.seq);
  return { ...raw, seq: Number.isFinite(parsed) ? parsed : undefined };
};

const toApiRowData = (rowData: MeterUploadRowData) => {
  if (rowData.seq == null || !Number.isFinite(Number(rowData.seq))) {
    return { ...rowData, seq: undefined };
  }
  return { ...rowData, seq: String(rowData.seq) };
};

const toApiRowInput = <T extends { rowData: MeterUploadRowData }>(input: T) => ({
  ...input,
  rowData: toApiRowData(input.rowData),
});

const toRow = (raw: any): MeterUploadRow => ({
  id: raw.id,
  rowData: fromApiRowData(raw.rowData),
  manualIntervention: raw.manualIntervention ?? undefined,
  status: toRowStatus(raw.status),
  failureReasonCode: raw.failureReasonCode ?? undefined,
  failureReasonDetail: raw.failureReasonDetail ?? undefined,
  // Persisted once the row ledgers; lets the ledgered row's note cell open the
  // real declaration note. Kept through the wire boundary rather than dropped.
  declarationId: raw.declarationId ?? undefined,
});

export type MeterUploadDraft = {
  id: string;
  level1ResourceId: string;
  level1ResourceName?: string;
  fileName?: string;
  status: MeterUploadStatusValue;
  updatedAt: string;
  rows: MeterUploadRow[];
};

const toDraft = (raw: any): MeterUploadDraft => ({
  id: raw.id,
  level1ResourceId: raw.level1ResourceId,
  level1ResourceName:
    raw.level1Resource?.name ?? raw.level1Resource?.identifier ?? undefined,
  fileName: raw.fileName ?? undefined,
  status: toParentStatus(raw.status),
  updatedAt: raw.updatedAt,
  rows: (raw.rows ?? []).map(toRow),
});

export type CreateDraftInput = {
  level1ResourceId: string;
  fileName?: string;
  rows: Array<{
    id?: string;
    rowData: MeterUploadRowData;
    manualIntervention?: ManualIntervention;
  }>;
};

export type UpdateDraftInput = {
  fileName?: string;
  updatedAt: string;
};

export type RowInput = {
  id?: string;
  rowData: MeterUploadRowData;
  manualIntervention?: ManualIntervention;
};

export const createMeterUploadDraft = async (
  input: CreateDraftInput,
): Promise<MeterUploadDraft> => {
  const payload = { ...input, rows: input.rows.map(toApiRowInput) };
  const { data } = await api.post("/admin/meterUploads", payload);
  return toDraft(data.meterUpload);
};

export const getMeterUploadDraft = async (
  id: string,
): Promise<MeterUploadDraft> => {
  const { data } = await api.get(`/admin/meterUploads/${id}`);
  return toDraft(data.meterUpload);
};

export const updateMeterUploadDraft = async (
  id: string,
  input: UpdateDraftInput,
): Promise<MeterUploadDraft> => {
  const { data } = await api.patch(`/admin/meterUploads/${id}`, input);
  return toDraft(data.meterUpload);
};

export const deleteMeterUploadDraft = async (id: string): Promise<void> => {
  await api.delete(`/admin/meterUploads/${id}`);
};

export const createMeterUploadRow = async (
  meterUploadId: string,
  input: RowInput,
): Promise<MeterUploadRow> => {
  const { data } = await api.post(
    `/admin/meterUploads/${meterUploadId}/rows`,
    toApiRowInput(input),
  );
  return toRow(data.row);
};

export const updateMeterUploadRow = async (
  meterUploadId: string,
  rowId: string,
  input: RowInput,
): Promise<MeterUploadRow> => {
  const { data } = await api.patch(
    `/admin/meterUploads/${meterUploadId}/rows/${rowId}`,
    toApiRowInput(input),
  );
  return toRow(data.row);
};

export const deleteMeterUploadRow = async (
  meterUploadId: string,
  rowId: string,
): Promise<void> => {
  await api.delete(`/admin/meterUploads/${meterUploadId}/rows/${rowId}`);
};

export const trialRunMeterUpload = async (input: {
  level1ResourceId: string;
  bulkMeterReadings: MeterUploadRowData[];
}): Promise<TrialRunServerRow[]> => {
  const payload = {
    ...input,
    bulkMeterReadings: input.bulkMeterReadings.map(toApiRowData),
  };
  const { data } = await api.post("/admin/trialRunBulkMeter", payload);
  return data.trialRun;
};

export const finalizeMeterUpload = async (
  id: string,
  rowIds: string[],
): Promise<void> => {
  // Fire-and-forget: the server returns 202 and ledgers detached. The operator refreshes
  // the upload to see rows settle; there is no synchronous result to consume.
  await api.post(`/admin/meterUploads/${id}/ledger`, { rowIds });
};

export const listMeterUploadsByLevel1Resource = async (
  level1ResourceId: string,
): Promise<any[]> => {
  const { data } = await api.get("/admin/meterUploads", {
    params: { level1ResourceId },
  });
  return data.meterUploads;
};

export const getBulkMeterTemplateRows = async (
  level1ResourceId: string,
): Promise<BulkMeterTemplateRow[]> => {
  const { data } = await api.get("/admin/bulkMeterTemplate", {
    params: { level1ResourceId },
  });
  return data.rows;
};
